"use client";

import { useRouter } from "next/navigation";
import { useState } from "react";
import { ApiError, describeError, login } from "@/lib/api";
import { BRAND } from "@/lib/branding";

/**
 * Tela de login.
 *
 * Client Component pelo mesmo motivo da home: o POST sai do NAVEGADOR direto
 * para o FastAPI em NEXT_PUBLIC_API_URL. Uma Server Action rodaria no servidor
 * do Next, que teria entao de repassar o Set-Cookie para o browser — isso e um
 * BFF, e e infraestrutura que este projeto nao precisa.
 *
 * Nao ha nada a guardar apos o login: o token vem num cookie HttpOnly que o
 * browser administra sozinho. Por isso a funcao so navega.
 *
 * A marca (nome e iniciais) vem de @/lib/branding, nunca de literais aqui: esta
 * e a primeira tela que a pessoa ve, e um "Valbot" esquecido no meio do JSX
 * sobreviveria a qualquer troca de marca feita no arquivo central.
 */
export default function LoginPage() {
  const router = useRouter();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (enviando) return;

    setEnviando(true);
    setErro(null);
    try {
      await login({ email: email.trim(), password: senha });
      // `enviando` fica true de proposito no caminho feliz: nao ha finally aqui.
      // Soltar o botao agora faria ele voltar a "Entrar" e reabilitar por uma
      // fracao de segundo ate a navegacao acontecer — tempo suficiente para um
      // duplo clique disparar um segundo login. O componente e desmontado logo
      // em seguida, entao o estado nao "vaza".
      //
      // replace e nao push: sem isso o botao "voltar" do browser devolve o
      // usuario ja logado para a tela de login.
      //
      // Destino "/home" e nao "/": a raiz virou apenas um redirect de
      // compatibilidade, e manda-lo aqui custaria um salto extra logo no
      // primeiro segundo de sessao.
      router.replace("/home");
      // Redesenha a arvore do servidor com o cookie novo ja valendo.
      router.refresh();
    } catch (error) {
      // 401 aqui e uso normal (senha errada), nao defeito: mostra a mensagem
      // generica do backend em vez do "(HTTP 401)" tecnico do describeError.
      setErro(
        error instanceof ApiError && error.isUnauthorized
          ? error.message
          : describeError(error),
      );
      setSenha("");
      setEnviando(false);
    }
  };

  return (
    <main className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center px-5 py-10">
      <header className="mb-8">
        {/* Mesmo avatar quadrado que abre o sidebar: quem entra aqui reconhece a
            marca de novo, no mesmo formato, assim que a area logada carrega.
            aria-hidden porque o <h1> logo abaixo ja diz o nome — o leitor de
            tela nao ganha nada ouvindo as iniciais antes. */}
        <div
          aria-hidden="true"
          className="grid size-11 place-items-center rounded-xl bg-brand-600 text-sm font-semibold text-white"
        >
          {BRAND.initials}
        </div>
        <h1 className="mt-4 text-2xl font-semibold tracking-tight">
          {BRAND.name}
        </h1>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          Entre para acessar o painel.
        </p>
      </header>

      {/* Anel de foco nos inputs: brand-600 no claro, brand-400 no escuro.
          Estes tons sao escolhidos por CONTRASTE, nao por estetica. Com a paleta
          cinza, o brand-300 (#d4d4d4) que estava aqui dava 1,48:1 contra o fundo
          branco do input e o brand-800 (#202020) dava 1,09:1 no tema escuro —
          nos dois casos, anel nenhum, e navegar o formulario por Tab virava
          adivinhacao. O minimo da WCAG 2.2 para indicador de foco e 3:1; os
          valores atuais ficam perto de 7,5:1 nos dois temas.
          Cuidado ao mexer: em Tailwind v4 uma classe de cor INEXISTENTE nao
          quebra o build, ela so nao e gerada, e o ring-2 cai no currentColor —
          a cor do texto do input. O erro fica silencioso. */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="email"
            className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
          >
            E-mail
          </label>
          <input
            id="email"
            name="email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="username"
            autoFocus
            disabled={enviando}
            className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition placeholder:text-neutral-500 focus:border-brand-500 focus:ring-2 focus:ring-brand-600 disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:placeholder:text-neutral-400 dark:focus:border-brand-400 dark:focus:ring-brand-400"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="senha"
            className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400"
          >
            Senha
          </label>
          <input
            id="senha"
            name="password"
            type="password"
            value={senha}
            onChange={(event) => setSenha(event.target.value)}
            required
            autoComplete="current-password"
            disabled={enviando}
            className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm outline-none transition placeholder:text-neutral-500 focus:border-brand-500 focus:ring-2 focus:ring-brand-600 disabled:opacity-60 dark:border-neutral-700 dark:bg-neutral-900 dark:placeholder:text-neutral-400 dark:focus:border-brand-400 dark:focus:ring-brand-400"
          />
        </div>

        {erro !== null && (
          <p
            role="alert"
            className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
          >
            {erro}
          </p>
        )}

        <button
          type="submit"
          disabled={enviando}
          className="mt-1 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {enviando ? "Entrando..." : "Entrar"}
        </button>
      </form>

      <p className="mt-8 text-center text-xs text-neutral-500 dark:text-neutral-400">
        Acesso restrito. Não há cadastro público.
      </p>
    </main>
  );
}
